import { format } from 'date-fns';

export type DateFormat = 'mdy' | 'dmy' | 'ymd';
export type TimeFormat = '12h' | '24h';

export interface DateFormatPreferences {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
}

const FORMAT_MAP: Record<DateFormat, Record<string, string>> = {
  mdy: {
    short: 'MMM d, yyyy',        // Jan 30, 2026
    long: 'MMMM d, yyyy',        // January 30, 2026
    weekday: 'EEEE, MMMM d, yyyy', // Thursday, January 30, 2026
    monthDay: 'MMM d',           // Jan 30
  },
  dmy: {
    short: 'd MMM yyyy',         // 30 Jan 2026
    long: 'd MMMM yyyy',         // 30 January 2026
    weekday: 'EEEE, d MMMM yyyy', // Thursday, 30 January 2026
    monthDay: 'd MMM',           // 30 Jan
  },
  ymd: {
    short: 'yyyy-MM-dd',         // 2026-01-30
    long: 'yyyy MMMM d',         // 2026 January 30
    weekday: 'EEEE, yyyy-MM-dd', // Thursday, 2026-01-30
    monthDay: 'MM-dd',           // 01-30
  },
};

const TIME_MAP: Record<TimeFormat, string> = {
  '12h': 'h:mm a',   // 2:30 PM
  '24h': 'HH:mm',    // 14:30
};

export type DateStyle = 'short' | 'long' | 'weekday' | 'monthDay';

export function getDateFormatString(
  dateFormat: DateFormat,
  style: DateStyle,
): string {
  return FORMAT_MAP[dateFormat][style];
}

export function getTimeFormatString(timeFormat: TimeFormat): string {
  return TIME_MAP[timeFormat];
}

export function getDateTimeFormatString(
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
  dateStyle: DateStyle = 'short',
): string {
  return `${FORMAT_MAP[dateFormat][dateStyle]} ${TIME_MAP[timeFormat]}`;
}

export function formatUserDate(
  date: Date | string,
  prefs: DateFormatPreferences,
  style: DateStyle = 'short',
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, getDateFormatString(prefs.dateFormat, style));
}

export function formatUserTime(
  date: Date | string,
  prefs: DateFormatPreferences,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, getTimeFormatString(prefs.timeFormat));
}

export function formatUserDateTime(
  date: Date | string,
  prefs: DateFormatPreferences,
  dateStyle: DateStyle = 'short',
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, getDateTimeFormatString(prefs.dateFormat, prefs.timeFormat, dateStyle));
}
